import React, { useState, useEffect } from 'react';
import { Building2, Users, TrendingUp, TrendingDown, ArrowUpRight, ArrowDownRight } from 'lucide-react';
import api from '../../utils/apiClient';

const OwnershipInsiderTradingSection = ({ symbol }) => {
  const [ownershipData, setOwnershipData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  useEffect(() => {
    if (symbol) {
      fetchOwnershipData();
    }
  }, [symbol]);

  const fetchOwnershipData = async () => {
    setLoading(true);
    setError(null);
    try {
      const response = await api.get(`/api/stocks/ownership/${symbol}`);
      if (response.data.success && (response.data.institutional || response.data.insider)) {
        setOwnershipData(response.data);
      } else {
        // Don't show error if data just isn't available - show message instead
        if (response.data.message) {
          setOwnershipData({ ...response.data, noDataAvailable: true });
        } else {
          setError('Failed to fetch ownership data');
        }
      }
    } catch (err) {
      console.error('Error fetching ownership data:', err);
      setError(err.response?.data?.message || 'Failed to load ownership data');
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
        <div className="animate-pulse">
          <div className="h-6 bg-gray-200 rounded w-1/4 mb-4"></div>
          <div className="h-32 bg-gray-200 rounded"></div>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
        <p className="text-red-600">Error loading ownership data: {error}</p>
      </div>
    );
  }

  if (!ownershipData) {
    return null;
  }

  // Show message if no data available instead of showing mock data
  if (ownershipData.noDataAvailable || (!ownershipData.institutional && !ownershipData.insider)) {
    return (
      <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
        <div className="text-center py-8">
          <p className="text-gray-600 mb-2">
            {ownershipData.message || 'Insider trading data is not currently available for this stock.'}
          </p>
          <p className="text-sm text-gray-500">
            Insider transaction data is fetched from SEC EDGAR (free, no API key required).
          </p>
        </div>
      </div>
    );
  }

  const { institutional, insider } = ownershipData;

  return (
    <div className="space-y-6">
      {/* Institutional Ownership Section */}
      {institutional && (
        <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
          <div className="flex items-center mb-6">
            <Building2 className="h-6 w-6 text-blue-600 mr-2" />
            <h3 className="text-xl font-bold text-gray-900">Institutional Ownership</h3>
          </div>

          {/* Summary Cards */}
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-6">
            <div className="p-4 bg-blue-50 rounded-lg">
              <p className="text-sm text-gray-600 mb-1">Total Ownership</p>
              <p className="text-3xl font-bold text-blue-600">{institutional.totalOwnership.toFixed(1)}%</p>
              <p className="text-xs text-gray-500 mt-1">{institutional.numberOfInstitutions.toLocaleString()} institutions</p>
            </div>
            <div className="p-4 bg-green-50 rounded-lg">
              <p className="text-sm text-gray-600 mb-1">Past Year Bought</p>
              <p className="text-2xl font-bold text-green-600">
                {(institutional.pastYear.sharesBought / 1000000).toFixed(1)}M shares
              </p>
            </div>
            <div className="p-4 bg-red-50 rounded-lg">
              <p className="text-sm text-gray-600 mb-1">Past Year Sold</p>
              <p className="text-2xl font-bold text-red-600">
                {(institutional.pastYear.sharesSold / 1000000).toFixed(1)}M shares
              </p>
            </div>
          </div>

          {/* Net Change */}
          <div className="mb-6 p-4 bg-gray-50 rounded-lg">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-600">Net Change (Past Year)</p>
                <p className={`text-2xl font-bold flex items-center ${
                  institutional.pastYear.netChange >= 0 ? 'text-green-600' : 'text-red-600'
                }`}>
                  {institutional.pastYear.netChange >= 0 ? (
                    <TrendingUp className="h-6 w-6 mr-2" />
                  ) : (
                    <TrendingDown className="h-6 w-6 mr-2" />
                  )}
                  {institutional.pastYear.netChange >= 0 ? '+' : ''}
                  {(institutional.pastYear.netChange / 1000000).toFixed(2)}M shares
                </p>
              </div>
              <div className={`px-4 py-2 rounded-full ${
                institutional.pastYear.netChange >= 0 
                  ? 'bg-green-100 text-green-800' 
                  : 'bg-red-100 text-red-800'
              }`}>
                {institutional.pastYear.netChange >= 0 ? '🟢 Bullish' : '🔴 Bearish'}
              </div>
            </div>
            <div className="mt-4 grid grid-cols-2 md:grid-cols-4 gap-4 text-sm">
              <div>
                <p className="text-gray-600">Institutions Buying</p>
                <p className="font-semibold text-green-600">{institutional.pastYear.institutionsBuying}</p>
              </div>
              <div>
                <p className="text-gray-600">Institutions Selling</p>
                <p className="font-semibold text-red-600">{institutional.pastYear.institutionsSelling}</p>
              </div>
              <div>
                <p className="text-gray-600">New Positions</p>
                <p className="font-semibold text-blue-600">{institutional.pastYear.newPositions}</p>
              </div>
              <div>
                <p className="text-gray-600">Closed Positions</p>
                <p className="font-semibold text-gray-600">{institutional.pastYear.closedPositions}</p>
              </div>
            </div>
          </div>

          {/* Top 10 Holders */}
          {institutional.topHolders && institutional.topHolders.length > 0 && (
            <div className="mb-6">
              <h4 className="text-lg font-semibold text-gray-900 mb-4">Top 10 Institutional Holders</h4>
              <div className="overflow-x-auto">
                <table className="min-w-full divide-y divide-gray-200">
                  <thead className="bg-gray-50">
                    <tr>
                      <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Rank</th>
                      <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Institution</th>
                      <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">Shares</th>
                      <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">% of Float</th>
                      <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">Value</th>
                      <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">Change</th>
                    </tr>
                  </thead>
                  <tbody className="bg-white divide-y divide-gray-200">
                    {institutional.topHolders.map((holder) => (
                      <tr key={holder.rank} className="hover:bg-gray-50">
                        <td className="px-4 py-3 whitespace-nowrap text-sm font-medium text-gray-900">{holder.rank}</td>
                        <td className="px-4 py-3 whitespace-nowrap text-sm text-gray-900">{holder.institutionName}</td>
                        <td className="px-4 py-3 whitespace-nowrap text-sm text-gray-900 text-right">
                          {(holder.shares / 1000000).toFixed(2)}M
                        </td>
                        <td className="px-4 py-3 whitespace-nowrap text-sm text-gray-900 text-right">
                          {holder.percentOfFloat.toFixed(2)}%
                        </td>
                        <td className="px-4 py-3 whitespace-nowrap text-sm text-gray-900 text-right">
                          ${(holder.value / 1000000000).toFixed(2)}B
                        </td>
                        <td className={`px-4 py-3 whitespace-nowrap text-sm text-right flex items-center justify-end ${
                          holder.changeFromPrevious >= 0 ? 'text-green-600' : 'text-red-600'
                        }`}>
                          {holder.changeFromPrevious >= 0 ? (
                            <ArrowUpRight className="h-4 w-4 mr-1" />
                          ) : (
                            <ArrowDownRight className="h-4 w-4 mr-1" />
                          )}
                          {holder.changeFromPrevious >= 0 ? '+' : ''}
                          {(holder.changeFromPrevious / 1000).toFixed(0)}K
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          )}

          {/* Quarterly Activity */}
          {institutional.quarterly && institutional.quarterly.length > 0 && (
            <div>
              <h4 className="text-lg font-semibold text-gray-900 mb-4">Quarterly Activity</h4>
              <div className="space-y-3">
                {institutional.quarterly.map((quarter, index) => (
                  <div key={index} className="p-4 bg-gray-50 rounded-lg">
                    <div className="flex items-center justify-between mb-2">
                      <span className="font-semibold text-gray-900">{quarter.quarter}</span>
                      <span className={`px-3 py-1 rounded-full text-sm font-medium ${
                        quarter.netChange >= 0 
                          ? 'bg-green-100 text-green-800' 
                          : 'bg-red-100 text-red-800'
                      }`}>
                        {quarter.netChange >= 0 ? '+' : ''}
                        {(quarter.netChange / 1000000).toFixed(2)}M net
                      </span>
                    </div>
                    <div className="grid grid-cols-3 gap-4 text-sm">
                      <div>
                        <p className="text-gray-600">Bought</p>
                        <p className="font-semibold text-green-600">
                          {(quarter.sharesBought / 1000000).toFixed(2)}M
                        </p>
                      </div>
                      <div>
                        <p className="text-gray-600">Sold</p>
                        <p className="font-semibold text-red-600">
                          {(quarter.sharesSold / 1000000).toFixed(2)}M
                        </p>
                      </div>
                      <div>
                        <p className="text-gray-600">Ownership</p>
                        <p className="font-semibold text-gray-900">
                          {quarter.ownershipPercent.toFixed(1)}%
                        </p>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>
      )}

      {/* Insider Trading Section */}
      {insider && (
        <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
          <div className="flex items-center mb-6">
            <Users className="h-6 w-6 text-purple-600 mr-2" />
            <h3 className="text-xl font-bold text-gray-900">Insider Trading</h3>
          </div>

          {/* Summary Cards */}
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-6">
            <div className="p-4 bg-purple-50 rounded-lg">
              <p className="text-sm text-gray-600 mb-1">Insider Ownership</p>
              <p className="text-3xl font-bold text-purple-600">{insider.totalOwnership.toFixed(1)}%</p>
              <p className="text-xs text-gray-500 mt-1">{insider.numberOfInsiders} insiders</p>
            </div>
            <div className="p-4 bg-green-50 rounded-lg">
              <p className="text-sm text-gray-600 mb-1">Past Year Bought</p>
              <p className="text-2xl font-bold text-green-600">
                {(insider.pastYear.sharesBought / 1000000).toFixed(2)}M shares
              </p>
              <p className="text-xs text-gray-500 mt-1">{insider.pastYear.buyTransactions} transactions</p>
            </div>
            <div className="p-4 bg-red-50 rounded-lg">
              <p className="text-sm text-gray-600 mb-1">Past Year Sold</p>
              <p className="text-2xl font-bold text-red-600">
                {(insider.pastYear.sharesSold / 1000000).toFixed(2)}M shares
              </p>
              <p className="text-xs text-gray-500 mt-1">{insider.pastYear.sellTransactions} transactions</p>
            </div>
          </div>

          {/* Net Change */}
          <div className="mb-6 p-4 bg-gray-50 rounded-lg">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-600">Net Change (Past Year)</p>
                <p className={`text-2xl font-bold flex items-center ${
                  insider.pastYear.netChange >= 0 ? 'text-green-600' : 'text-red-600'
                }`}>
                  {insider.pastYear.netChange >= 0 ? (
                    <TrendingUp className="h-6 w-6 mr-2" />
                  ) : (
                    <TrendingDown className="h-6 w-6 mr-2" />
                  )}
                  {insider.pastYear.netChange >= 0 ? '+' : ''}
                  {(insider.pastYear.netChange / 1000000).toFixed(2)}M shares
                </p>
              </div>
              <div className={`px-4 py-2 rounded-full ${
                insider.pastYear.netChange >= 0 
                  ? 'bg-green-100 text-green-800' 
                  : 'bg-red-100 text-red-800'
              }`}>
                {insider.pastYear.netChange >= 0 ? '🟢 Bullish' : '🔴 Bearish'}
              </div>
            </div>
            {insider.pastYear.largestTransaction && (
              <div className="mt-4 p-3 bg-white rounded border border-gray-200">
                <p className="text-sm text-gray-600 mb-1">Largest Transaction</p>
                <p className="font-semibold text-gray-900">
                  {insider.pastYear.largestTransaction.insider} - {insider.pastYear.largestTransaction.type}
                </p>
                <p className="text-sm text-gray-600">
                  {insider.pastYear.largestTransaction.shares.toLocaleString()} shares @ ${insider.pastYear.largestTransaction.value.toLocaleString()}
                </p>
              </div>
            )}
          </div>

          {/* Recent Transactions */}
          {insider.recentTransactions && insider.recentTransactions.length > 0 && (
            <div>
              <h4 className="text-lg font-semibold text-gray-900 mb-4">Recent Insider Transactions (Last 30 Days)</h4>
              <div className="overflow-x-auto">
                <table className="min-w-full divide-y divide-gray-200">
                  <thead className="bg-gray-50">
                    <tr>
                      <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Date</th>
                      <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Insider</th>
                      <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Position</th>
                      <th className="px-4 py-3 text-center text-xs font-medium text-gray-500 uppercase tracking-wider">Type</th>
                      <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">Shares</th>
                      <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">Price</th>
                      <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">Value</th>
                    </tr>
                  </thead>
                  <tbody className="bg-white divide-y divide-gray-200">
                    {insider.recentTransactions.map((transaction, index) => (
                      <tr key={index} className="hover:bg-gray-50">
                        <td className="px-4 py-3 whitespace-nowrap text-sm text-gray-900">
                          {new Date(transaction.date).toLocaleDateString()}
                        </td>
                        <td className="px-4 py-3 whitespace-nowrap text-sm font-medium text-gray-900">
                          {transaction.insiderName}
                        </td>
                        <td className="px-4 py-3 whitespace-nowrap text-sm text-gray-600">
                          {transaction.position}
                        </td>
                        <td className="px-4 py-3 whitespace-nowrap text-center">
                          <span className={`px-2 py-1 rounded-full text-xs font-medium ${
                            transaction.transactionType.toLowerCase().includes('buy') || 
                            transaction.transactionType.toLowerCase().includes('purchase')
                              ? 'bg-green-100 text-green-800'
                              : 'bg-red-100 text-red-800'
                          }`}>
                            {transaction.transactionType}
                          </span>
                        </td>
                        <td className="px-4 py-3 whitespace-nowrap text-sm text-gray-900 text-right">
                          {transaction.shares.toLocaleString()}
                        </td>
                        <td className="px-4 py-3 whitespace-nowrap text-sm text-gray-900 text-right">
                          ${transaction.pricePerShare.toFixed(2)}
                        </td>
                        <td className="px-4 py-3 whitespace-nowrap text-sm font-semibold text-gray-900 text-right">
                          ${(transaction.value / 1000000).toFixed(2)}M
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          )}
        </div>
      )}

      {/* Data Source Note */}
      {ownershipData.note && (
        <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4">
          <p className="text-sm text-yellow-800">
            <strong>Note:</strong> {ownershipData.note}
          </p>
        </div>
      )}
    </div>
  );
};

export default OwnershipInsiderTradingSection;


